import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Search, Filter, Grid, List, MoreVertical, UserPlus, Download, Upload, RefreshCw, MessageCircle, Bell, Image, Calendar, X, Users, ChevronDown, Send } from 'lucide-react';
import Button from '../UI/Button';

interface SelectedGuestName {
  id: string;
  name: string;
}

interface GuestActionBarProps {
  searchTerm: string;
  onSearchChange: (term: string) => void;
  statusFilter: string[];
  onStatusFilterChange: (status: string[]) => void;
  importFilter: string;
  onImportFilterChange: (filter: string) => void;
  viewMode: 'grid' | 'table' | 'list';
  onViewModeChange: (mode: 'grid' | 'table' | 'list') => void;
  sortBy: string;
  onSortChange: (sort: string) => void;
  selectedCount: number;
  totalCount: number;
  hasWhatsAppSentInSelection?: boolean;
  onAddGuest: () => void;
  onImportGuests: () => void;
  onExportGuests: () => void;
  onBulkAction: (action: string, newStatus?: string) => void;
  onSendWhatsApp: () => void;
  onSendReminders: () => void;
  onSendFollowUp: () => void;
  onSendSaveTheDate: () => void;
  onSendImageMessage: () => void;
  onClearSelection: () => void;
  onGetSelectedGuestNames: () => Promise<SelectedGuestName[]>;
  onRefresh: () => void;
  loading?: boolean;
  exporting?: boolean;
  theme: 'dark' | 'light';
  eventHasEnded?: boolean;
  guestLimit?: number;
  currentGuestCount?: number;
}

interface FilterOption {
  value: string;
  label: string;
  count?: number;
  color?: string;
}

export const GuestActionBar: React.FC<GuestActionBarProps> = ({
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  importFilter,
  onImportFilterChange,
  viewMode,
  onViewModeChange,
  sortBy,
  onSortChange,
  selectedCount,
  totalCount,
  hasWhatsAppSentInSelection = false,
  onAddGuest,
  onImportGuests,
  onExportGuests,
  onBulkAction,
  onSendWhatsApp,
  onSendReminders,
  onSendFollowUp,
  onSendSaveTheDate,
  onSendImageMessage,
  onClearSelection,
  onGetSelectedGuestNames,
  onRefresh,
  loading = false,
  exporting = false,
  theme,
  eventHasEnded = false,
  guestLimit,
  currentGuestCount = 0
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showMobileActions, setShowMobileActions] = useState(false);
  const bulkActionsRef = useRef<HTMLDivElement>(null);
  const filtersRef = useRef<HTMLDivElement>(null);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const mobileActionsRef = useRef<HTMLDivElement>(null);
  const selectedNamesRef = useRef<HTMLDivElement>(null);
  const [showSelectedNames, setShowSelectedNames] = useState(false);
  const [selectedNames, setSelectedNames] = useState<SelectedGuestName[]>([]);
  const [loadingSelectedNames, setLoadingSelectedNames] = useState(false);

  useEffect(() => {
    setShowSelectedNames(false);
    setSelectedNames([]);
  }, [selectedCount]);

  useEffect(() => {
    if (!showSelectedNames) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (selectedNamesRef.current && !selectedNamesRef.current.contains(event.target as Node)) {
        setShowSelectedNames(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSelectedNames]);

  const toggleSelectedNames = async () => {
    if (showSelectedNames) {
      setShowSelectedNames(false);
      return;
    }

    setShowSelectedNames(true);

    if (selectedNames.length > 0) {
      return;
    }

    setLoadingSelectedNames(true);
    try {
      const names = await onGetSelectedGuestNames();
      setSelectedNames(names);
    } finally {
      setLoadingSelectedNames(false);
    }
  };

  // Handle body scroll locking and escape key for mobile actions modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showMobileActions) {
        setShowMobileActions(false);
      }
    };

    if (showMobileActions) {
      document.addEventListener('keydown', handleEscape);
      
      // Save original styles and scroll position
      const originalBodyOverflow = document.body.style.overflow || '';
      const originalBodyPosition = document.body.style.position || '';
      const originalBodyWidth = document.body.style.width || '';
      const originalHtmlOverflow = document.documentElement.style.overflow || '';
      const savedScrollPosition = window.scrollY;
      
      // Prevent scrolling
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${savedScrollPosition}px`;
      document.documentElement.style.overflow = 'hidden';

      return () => {
        document.removeEventListener('keydown', handleEscape);
        
        // Restore original styles
        document.body.style.overflow = originalBodyOverflow;
        document.body.style.position = originalBodyPosition;
        document.body.style.width = originalBodyWidth;
        document.body.style.top = '';
        document.documentElement.style.overflow = originalHtmlOverflow;
        
        // Restore scroll position
        window.scrollTo(0, savedScrollPosition);
      };
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showMobileActions]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Handle bulk actions dropdown
      if (bulkActionsRef.current && !bulkActionsRef.current.contains(target)) {
        setShowBulkActions(false);
      }
      
      // Handle filters panel - don't close if clicking the filter button
      if (filtersRef.current && !filtersRef.current.contains(target)) {
        if (filterButtonRef.current && !filterButtonRef.current.contains(target)) {
          setShowFilters(false);
        }
      }
    };

    if (showBulkActions || showFilters) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showBulkActions, showFilters]);

  // Guest limit is now informational only - no enforcement

  const statusOptions: FilterOption[] = [
    { value: 'all', label: 'All Statuses', color: 'text-gray-600' },
    { value: 'pending', label: 'Pending', color: 'text-yellow-600' },
    { value: 'accepted', label: 'Confirmed', color: 'text-green-600' },
    { value: 'rejected', label: 'Declined', color: 'text-red-600' },
    { value: 'ignored', label: 'No Response', color: 'text-gray-600' }
  ];

  const importOptions: FilterOption[] = [
    { value: 'all', label: 'All Sources' },
    { value: 'imported', label: 'Imported' },
    { value: 'manual', label: 'Manual' }
  ];

  const sortOptions: FilterOption[] = [
    { value: '-id', label: 'Recently Added', color: 'text-blue-600' },
    { value: 'guest__first_name', label: 'Name (A-Z)', color: 'text-purple-600' },
    { value: '-guest__first_name', label: 'Name (Z-A)', color: 'text-purple-600' },
    { value: '-response_at', label: 'Response Date (New)', color: 'text-green-600' },
    { value: 'response_at', label: 'Response Date (Old)', color: 'text-green-600' }
  ];

  const viewModeOptions = [
    { value: 'grid' as const, icon: Grid, label: 'Grid View' },
    { value: 'table' as const, icon: List, label: 'Table View' },
    { value: 'list' as const, icon: List, label: 'List View' }
  ];

  return (
    <div className="space-y-4">
      {/* Search and Filters Section */}
      <div className={`rounded-xl shadow-sm border p-3 ${
        theme === 'dark' 
          ? 'bg-gray-800 border-gray-700' 
          : 'bg-white border-gray-200'
      }`}>
        <div className="flex flex-col sm:flex-row gap-2 items-center sm:justify-between">
          {/* Search */}
          <div className="relative flex-1 w-full sm:mr-2">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
            }`} />
            <input
              type="text"
              placeholder="Search guests..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className={`w-full pl-9 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm ${
                theme === 'dark'
                  ? 'border-gray-600 bg-gray-700 text-white placeholder-gray-400'
                  : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
              }`}
            />
            {searchTerm && (
              <button
                onClick={() => onSearchChange('')}
                className={`absolute right-3 top-1/2 transform -translate-y-1/2 hover:text-gray-600 ${
                  theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                ×
              </button>
            )}
          </div>

          {/* Quick Filters */}
          <div className="flex gap-1.5 flex-shrink-0">
            {/* Compact Filter Toggle Button */}
            <div className="relative">
              <button
                ref={filterButtonRef}
                onClick={() => {
                  setShowFilters(!showFilters);
                }}
                className={`flex items-center gap-1.5 px-2.5 py-2 border rounded-lg text-xs font-medium transition-all duration-150 ${
                  statusFilter.length > 0 || importFilter !== 'all'
                    ? (theme === 'dark'
                        ? 'bg-blue-900/30 border-blue-700/50 text-blue-300'
                        : 'bg-blue-50 border-blue-200 text-blue-700')
                    : (theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50')
                }`}
              >
                <Filter className={`h-3.5 w-3.5 ${
                  statusFilter.length > 0 || importFilter !== 'all' ? 'animate-pulse' : ''
                }`} />
                <span>Filters</span>
                {(statusFilter.length > 0 || importFilter !== 'all') && (
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                    theme === 'dark' 
                      ? 'bg-blue-800/50 text-blue-300' 
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {statusFilter.length + (importFilter !== 'all' ? 1 : 0)}
                  </span>
                )}
              </button>
            </div>

            {/* View Mode Toggle */}
            <div className={`flex rounded-lg overflow-hidden ${
              theme === 'dark' ? 'border border-gray-600' : 'border border-gray-300'
            }`}>
              {viewModeOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.value}
                    onClick={() => onViewModeChange(option.value)}
                    className={`px-2.5 py-2 transition-colors ${
                      viewMode === option.value
                        ? (theme === 'dark'
                            ? 'bg-blue-600 text-white'
                            : 'bg-blue-500 text-white')
                        : (theme === 'dark'
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            : 'bg-white text-gray-600 hover:bg-gray-50')
                    }`}
                    title={option.label}
                  >
                    <Icon className="h-4 w-4" />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons Section */}
      <div className={`rounded-xl shadow-sm border p-3 ${
        theme === 'dark' 
          ? 'bg-gray-800 border-gray-700' 
          : 'bg-white border-gray-200'
      }`}>
        {/* Mobile Layout: Only show primary actions */}
        <div className="flex items-center gap-2 sm:hidden">
          {/* Refresh */}
          <button
            onClick={onRefresh}
            disabled={loading}
            className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
              theme === 'dark'
                ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>

          <div className="flex flex-1 items-center gap-2 min-w-0">
            <Button
              variant="secondary"
              onClick={onImportGuests}
              disabled={eventHasEnded}
              title={eventHasEnded ? "Cannot import guests to events that have already ended" : ""}
              className="flex-1 flex items-center justify-center px-3 py-2 text-sm min-w-0"
            >
              <Upload className="h-4 w-4 mr-1.5 flex-shrink-0" />
              Import
            </Button>
            <Button
              variant="primary"
              onClick={onAddGuest}
              disabled={eventHasEnded}
              title={
                eventHasEnded
                  ? "Cannot add guests to events that have already ended"
                  : ""
              }
              className="flex-1 flex items-center justify-center px-3 py-2 text-sm font-medium min-w-0"
              style={eventHasEnded ? { cursor: 'not-allowed', pointerEvents: 'auto' } : undefined}
            >
              <UserPlus className="h-4 w-4 mr-1.5 flex-shrink-0" />
              Add Guest
            </Button>
          </div>

          {/* More Actions Button - Opens Bottom Sheet */}
          <button
            onClick={() => setShowMobileActions(!showMobileActions)}
            className={`p-2 rounded-lg transition-colors ${
              theme === 'dark'
                ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
            title="More Actions"
          >
            <MoreVertical className="h-5 w-5" />
          </button>
        </div>

        {/* Desktop Layout: Show all buttons */}
        <div className="hidden sm:flex items-center gap-2 flex-wrap">
          {/* Refresh */}
          <button
            onClick={onRefresh}
            disabled={loading}
            className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
              theme === 'dark'
                ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>

          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={onImportGuests}
              className="flex items-center px-3 py-2 text-sm"
              disabled={eventHasEnded}
              title={eventHasEnded ? "Cannot import guests to events that have already ended" : ""}
            >
              <Upload className="h-4 w-4 mr-1.5" />
              Import
            </Button>

            <Button
              variant="primary"
              onClick={onAddGuest}
              disabled={eventHasEnded}
              title={
                eventHasEnded
                  ? "Cannot add guests to events that have already ended"
                  : ""
              }
              className="flex items-center px-4 py-2 text-sm font-medium"
              style={eventHasEnded ? { cursor: 'not-allowed', pointerEvents: 'auto' } : undefined}
            >
              <UserPlus className="h-4 w-4 mr-1.5" />
              Add Guest
            </Button>
          </div>

          <Button
            variant="secondary"
            onClick={onSendWhatsApp}
            className="flex items-center px-3 py-2 text-sm"
            disabled={selectedCount === 0 || hasWhatsAppSentInSelection || eventHasEnded}
            title={eventHasEnded ? "Cannot send invitations for events that have already ended" : (hasWhatsAppSentInSelection ? "Some selected guests already have an invitation sent" : "")}
          >
            <MessageCircle className="h-4 w-4 mr-1.5" />
            Invite
          </Button>

          <Button
            variant="secondary"
            onClick={onSendReminders}
            className="flex items-center px-3 py-2 text-sm"
            disabled={selectedCount === 0 || eventHasEnded}
            title={eventHasEnded ? "Cannot send reminders for events that have already ended" : (selectedCount === 0 ? "Select guests to send reminders" : "")}
          >
            <Bell className="h-4 w-4 mr-1.5" />
            Reminder
          </Button>

          <Button
            variant="secondary"
            onClick={onSendFollowUp}
            className="flex items-center px-3 py-2 text-sm"
            disabled={selectedCount === 0 || eventHasEnded}
            title={eventHasEnded ? "Cannot send follow-ups for events that have already ended" : (selectedCount === 0 ? "Select guests to follow up with" : "Message only the guests and companions who haven't responded yet")}
          >
            <Send className="h-4 w-4 mr-1.5" />
            Follow Up
          </Button>

          <Button
            variant="secondary"
            onClick={onSendSaveTheDate}
            className="flex items-center px-3 py-2 text-sm"
            disabled={selectedCount === 0 || eventHasEnded}
            title={eventHasEnded ? "Cannot send save the date messages for events that have already ended" : (selectedCount === 0 ? "Select guests to send save the date messages" : "")}
          >
            <Calendar className="h-4 w-4 mr-1.5" />
            Save the Date
          </Button>

          <Button
            variant="secondary"
            onClick={onSendImageMessage}
            className="flex items-center px-3 py-2 text-sm"
            disabled={selectedCount === 0 || eventHasEnded}
            title={eventHasEnded ? "Cannot send image messages for events that have already ended" : (selectedCount === 0 ? "Select guests to send image message" : "")}
          >
            <Image className="h-4 w-4 mr-1.5" />
            Send Image
          </Button>

          <Button
            type="button"
            variant="secondary"
            disabled
            className="flex items-center px-3 py-2 text-sm min-w-[2.75rem]"
            aria-label="Message templates (coming soon)"
            title="Coming soon"
          >
            {'\u00A0'}
          </Button>

          {/* More Actions */}
          <div className="relative" ref={bulkActionsRef}>
            <button
              onClick={() => setShowBulkActions(!showBulkActions)}
              className={`p-2 rounded-lg transition-colors ${
                theme === 'dark'
                  ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <MoreVertical className="h-4 w-4" />
            </button>
            
            {/* Bulk Actions Dropdown */}
            {showBulkActions && (
              <div className={`absolute right-0 top-full mt-2 w-48 rounded-lg shadow-lg py-1 z-50 ${
                theme === 'dark' 
                  ? 'bg-gray-800 border border-gray-700' 
                  : 'bg-white border border-gray-200'
              }`}>
                <button
                  onClick={() => {
                    onExportGuests();
                    setShowBulkActions(false);
                  }}
                  disabled={exporting}
                  className={`flex items-center w-full px-4 py-2 text-sm transition-colors ${
                    exporting 
                      ? 'opacity-50 cursor-not-allowed'
                      : theme === 'dark' 
                        ? 'text-gray-300 hover:bg-gray-700' 
                        : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Download className={`h-4 w-4 mr-2 ${exporting ? 'animate-pulse' : ''}`} />
                  {exporting ? 'Exporting...' : 'Export to Excel'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Actions Modal */}
      {showMobileActions && (() => {
        const handleOverlayClick = (e: React.MouseEvent) => {
          if (e.target === e.currentTarget) {
            setShowMobileActions(false);
          }
        };

        const handleModalScroll = (e: React.WheelEvent) => {
          e.stopPropagation();
        };

        const handleModalTouchMove = (e: React.TouchEvent) => {
          e.stopPropagation();
        };

        const modalContent = (
          <div 
            className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4 sm:hidden"
            onWheel={handleModalScroll}
            onTouchMove={handleModalTouchMove}
            onClick={handleOverlayClick}
          >
            {/* Overlay */}
            <div
              className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={handleOverlayClick}
            />
            
            {/* Modal */}
            <div 
              ref={mobileActionsRef}
              className="relative w-full max-w-sm max-h-[90vh] overflow-hidden overscroll-contain"
              onWheel={handleModalScroll}
              onTouchMove={handleModalTouchMove}
            >
              <div className={`w-full rounded-2xl shadow-2xl ${
                theme === 'dark' 
                  ? 'bg-gray-800 border border-gray-700' 
                  : 'bg-white border border-gray-200'
              }`}>
                {/* Header */}
                <div className={`flex items-center justify-between px-4 py-3 border-b flex-shrink-0 ${
                  theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}>
                  <h3 className={`text-base font-semibold ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    Actions
                  </h3>
                  <button
                    onClick={() => setShowMobileActions(false)}
                    className={`p-1.5 rounded-lg transition-colors ${
                      theme === 'dark' 
                        ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' 
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {/* Action Buttons Grid - Scrollable */}
                <div 
                  className="p-3 sm:p-4 overflow-y-auto"
                  style={{ 
                    maxHeight: 'calc(90vh - 60px)',
                    WebkitOverflowScrolling: 'touch',
                    overscrollBehavior: 'contain'
                  }}
                >
                  <div className="grid grid-cols-2 gap-2.5">
                {/* Invite */}
                <button
                  onClick={() => {
                    onSendWhatsApp();
                    setShowMobileActions(false);
                  }}
                  disabled={selectedCount === 0 || hasWhatsAppSentInSelection || eventHasEnded}
                  className={`flex flex-col items-center justify-center p-3 sm:p-4 rounded-xl border transition-all min-h-[100px] ${
                    selectedCount === 0 || hasWhatsAppSentInSelection || eventHasEnded
                      ? 'opacity-50 cursor-not-allowed'
                      : theme === 'dark'
                        ? 'bg-gray-700/50 border-gray-600 hover:bg-gray-700 hover:border-gray-500'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300'
                  }`}
                >
                  <MessageCircle className={`h-5 w-5 sm:h-6 sm:w-6 mb-1.5 ${
                    theme === 'dark' ? 'text-green-400' : 'text-green-600'
                  }`} />
                  <span className={`text-xs sm:text-sm font-medium text-center leading-tight ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Invite
                  </span>
                </button>

                {/* Reminder */}
                <button
                  onClick={() => {
                    onSendReminders();
                    setShowMobileActions(false);
                  }}
                  disabled={selectedCount === 0 || eventHasEnded}
                  className={`flex flex-col items-center justify-center p-3 sm:p-4 rounded-xl border transition-all min-h-[100px] ${
                    selectedCount === 0 || eventHasEnded
                      ? 'opacity-50 cursor-not-allowed'
                      : theme === 'dark'
                        ? 'bg-gray-700/50 border-gray-600 hover:bg-gray-700 hover:border-gray-500'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300'
                  }`}
                >
                  <Bell className={`h-5 w-5 sm:h-6 sm:w-6 mb-1.5 ${
                    theme === 'dark' ? 'text-purple-400' : 'text-purple-600'
                  }`} />
                  <span className={`text-xs sm:text-sm font-medium text-center leading-tight ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Reminder
                  </span>
                </button>

                {/* Follow Up */}
                <button
                  onClick={() => {
                    onSendFollowUp();
                    setShowMobileActions(false);
                  }}
                  disabled={selectedCount === 0 || eventHasEnded}
                  className={`flex flex-col items-center justify-center p-3 sm:p-4 rounded-xl border transition-all min-h-[100px] ${
                    selectedCount === 0 || eventHasEnded
                      ? 'opacity-50 cursor-not-allowed'
                      : theme === 'dark'
                        ? 'bg-gray-700/50 border-gray-600 hover:bg-gray-700 hover:border-gray-500'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300'
                  }`}
                >
                  <Send className={`h-5 w-5 sm:h-6 sm:w-6 mb-1.5 ${
                    theme === 'dark' ? 'text-teal-400' : 'text-teal-600'
                  }`} />
                  <span className={`text-xs sm:text-sm font-medium text-center leading-tight ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Follow Up
                  </span>
                </button>

                {/* Save the Date */}
                <button
                  onClick={() => {
                    onSendSaveTheDate();
                    setShowMobileActions(false);
                  }}
                  disabled={selectedCount === 0 || eventHasEnded}
                  className={`flex flex-col items-center justify-center p-3 sm:p-4 rounded-xl border transition-all min-h-[100px] ${
                    selectedCount === 0 || eventHasEnded
                      ? 'opacity-50 cursor-not-allowed'
                      : theme === 'dark'
                        ? 'bg-gray-700/50 border-gray-600 hover:bg-gray-700 hover:border-gray-500'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300'
                  }`}
                >
                  <Calendar className={`h-5 w-5 sm:h-6 sm:w-6 mb-1.5 ${
                    theme === 'dark' ? 'text-orange-400' : 'text-orange-600'
                  }`} />
                  <span className={`text-xs sm:text-sm font-medium text-center leading-tight ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Save Date
                  </span>
                </button>

                {/* Send Image */}
                <button
                  onClick={() => {
                    onSendImageMessage();
                    setShowMobileActions(false);
                  }}
                  disabled={selectedCount === 0 || eventHasEnded}
                  className={`flex flex-col items-center justify-center p-3 sm:p-4 rounded-xl border transition-all min-h-[100px] ${
                    selectedCount === 0 || eventHasEnded
                      ? 'opacity-50 cursor-not-allowed'
                      : theme === 'dark'
                        ? 'bg-gray-700/50 border-gray-600 hover:bg-gray-700 hover:border-gray-500'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300'
                  }`}
                >
                  <Image className={`h-5 w-5 sm:h-6 sm:w-6 mb-1.5 ${
                    theme === 'dark' ? 'text-pink-400' : 'text-pink-600'
                  }`} />
                  <span className={`text-xs sm:text-sm font-medium text-center leading-tight ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Send Image
                  </span>
                </button>

                <button
                  type="button"
                  disabled
                  aria-label="Message templates (coming soon)"
                  title="Coming soon"
                  className={`flex flex-col items-center justify-center p-3 sm:p-4 rounded-xl border min-h-[100px] opacity-50 cursor-not-allowed ${
                    theme === 'dark'
                      ? 'bg-gray-700/30 border-gray-600'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <span className="text-[10px] sm:text-xs font-medium text-transparent select-none" aria-hidden>
                    {'\u00A0'}
                  </span>
                </button>

                {/* Export */}
                <button
                  onClick={() => {
                    onExportGuests();
                    setShowMobileActions(false);
                  }}
                  disabled={exporting}
                  className={`flex flex-col items-center justify-center p-3 sm:p-4 rounded-xl border transition-all min-h-[100px] ${
                    exporting
                      ? 'opacity-50 cursor-not-allowed'
                      : theme === 'dark'
                        ? 'bg-gray-700/50 border-gray-600 hover:bg-gray-700 hover:border-gray-500'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300'
                  }`}
                >
                  <Download className={`h-5 w-5 sm:h-6 sm:w-6 mb-1.5 ${exporting ? 'animate-pulse' : ''} ${
                    theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'
                  }`} />
                  <span className={`text-xs sm:text-sm font-medium text-center leading-tight ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {exporting ? 'Exporting...' : 'Export'}
                  </span>
                </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

        return createPortal(modalContent, document.body);
      })()}

      {/* Compact Enhanced Filter Panel */}
      {showFilters && (
        <div ref={filtersRef} className={`rounded-lg shadow-lg border p-4 space-y-3 ${
          theme === 'dark' 
            ? 'bg-gray-800 border-gray-700' 
            : 'bg-white border-gray-200'
        }`}>
          {/* Compact Filter Header */}
          <div className="flex items-center justify-between">
            <h3 className={`text-sm font-semibold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Filters
            </h3>
            <button
              onClick={() => setShowFilters(false)}
              className={`p-1 rounded hover:bg-gray-100 transition-colors ${
                theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Status & Source Filters - Side by Side */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Status Filter - Multi-select with checkboxes */}
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Status {statusFilter.length > 0 && (
                  <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] ${
                    theme === 'dark' ? 'bg-blue-800/50 text-blue-300' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {statusFilter.length} selected
                  </span>
                )}
              </label>
              <div className="flex flex-wrap gap-1.5">
                {statusOptions.filter(opt => opt.value !== 'all').map((option) => {
                  const isSelected = statusFilter.includes(option.value);
                  return (
                    <label
                      key={option.value}
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-150 cursor-pointer ${
                        isSelected
                          ? option.value === 'accepted'
                            ? 'bg-green-500 text-white shadow-sm'
                            : option.value === 'rejected'
                            ? 'bg-red-500 text-white shadow-sm'
                            : option.value === 'pending'
                            ? 'bg-yellow-500 text-white shadow-sm'
                            : option.value === 'ignored'
                            ? 'bg-gray-500 text-white shadow-sm'
                            : 'bg-blue-500 text-white shadow-sm'
                          : theme === 'dark'
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            onStatusFilterChange([...statusFilter, option.value]);
                          } else {
                            onStatusFilterChange(statusFilter.filter(s => s !== option.value));
                          }
                        }}
                        className="h-3 w-3 rounded cursor-pointer"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span>{option.label}</span>
                    </label>
                  );
                })}
              </div>
              {statusFilter.length > 0 && (
                <button
                  onClick={() => onStatusFilterChange([])}
                  className={`mt-2 text-xs text-blue-600 hover:text-blue-700 underline ${
                    theme === 'dark' ? 'text-blue-400 hover:text-blue-300' : ''
                  }`}
                >
                  Clear all
                </button>
              )}
            </div>

            {/* Source Filter */}
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Source
              </label>
              <div className="flex flex-wrap gap-1.5">
                {importOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      onImportFilterChange(option.value);
                    }}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-150 ${
                      importFilter === option.value
                        ? 'bg-purple-500 text-white shadow-sm'
                        : theme === 'dark'
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Sort By Filter - Compact Cards */}
          <div>
            <label className={`block text-xs font-medium mb-1.5 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Sort By
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {sortOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    onSortChange(option.value);
                  }}
                  className={`flex items-center justify-between px-3 py-1.5 rounded text-xs font-medium transition-all duration-150 ${
                    sortBy === option.value
                      ? theme === 'dark'
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'bg-blue-500 text-white shadow-sm'
                      : theme === 'dark'
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span className="truncate">{option.label}</span>
                  {sortBy === option.value && (
                    <svg className="w-3 h-3 ml-1.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Compact Filter Actions */}
          <div className={`flex items-center justify-between pt-2 border-t ${
            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <span className={`text-xs ${
              theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
            }`}>
              {statusFilter.length > 0 || importFilter !== 'all' 
                ? 'Filters active' 
                : 'No filters'}
            </span>
            <button
              onClick={() => {
                onStatusFilterChange([]);
                onImportFilterChange('all');
                onSortChange('-id');
                setShowFilters(false);
              }}
              className={`px-3 py-1 rounded text-xs font-medium transition-all duration-150 ${
                theme === 'dark'
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Reset
            </button>
          </div>
        </div>
      )}

      {/* Enhanced Selection Bar */}
      {selectedCount > 0 && (
        <div className={`rounded-2xl p-3 sm:p-4 shadow-lg animate-in slide-in-from-top duration-300 ${
          theme === 'dark' 
            ? 'bg-gradient-to-r from-blue-900/30 to-indigo-900/30 border-2 border-blue-700/50' 
            : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200'
        }`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            {/* Info Section */}
            <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
              <div className={`h-8 w-8 sm:h-10 sm:w-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                theme === 'dark' ? 'bg-blue-800/50' : 'bg-blue-100'
              }`}>
                <span className={`font-bold text-xs sm:text-sm ${
                  theme === 'dark' ? 'text-blue-300' : 'text-blue-600'
                }`}>{selectedCount}</span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className={`text-xs sm:text-sm font-semibold ${
                    theme === 'dark' ? 'text-blue-200' : 'text-blue-900'
                  }`}>
                    {selectedCount} guest{selectedCount > 1 ? 's' : ''} selected
                  </span>
                  <div className="relative" ref={selectedNamesRef}>
                    <button
                      type="button"
                      onClick={() => void toggleSelectedNames()}
                      aria-expanded={showSelectedNames}
                      aria-label="View selected guest names"
                      title="View who is selected"
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] sm:text-xs font-medium border transition-colors ${
                        showSelectedNames
                          ? theme === 'dark'
                            ? 'bg-blue-900/50 text-blue-100 border-blue-600'
                            : 'bg-blue-100 text-blue-800 border-blue-300'
                          : theme === 'dark'
                            ? 'text-blue-200 border-blue-700/60 hover:bg-blue-900/40 hover:border-blue-600'
                            : 'text-blue-700 border-blue-200 hover:bg-white hover:border-blue-300'
                      }`}
                    >
                      <Users className="h-3.5 w-3.5 shrink-0" />
                      <span>View names</span>
                      <ChevronDown
                        className={`h-3 w-3 shrink-0 transition-transform duration-200 ${
                          showSelectedNames ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {showSelectedNames && (
                      <div
                        className={`absolute left-0 top-full z-50 mt-1.5 w-[min(100vw-2rem,16rem)] sm:w-64 rounded-lg border shadow-lg ${
                          theme === 'dark'
                            ? 'bg-gray-900 border-gray-700'
                            : 'bg-white border-gray-200'
                        }`}
                      >
                        <div className={`px-3 py-2 border-b text-xs font-semibold ${
                          theme === 'dark'
                            ? 'border-gray-700 text-gray-200'
                            : 'border-gray-100 text-gray-800'
                        }`}>
                          Selected guests
                        </div>
                        <div className="max-h-44 overflow-y-auto py-1">
                          {loadingSelectedNames ? (
                            <p className={`px-3 py-2 text-xs ${
                              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              Loading...
                            </p>
                          ) : selectedNames.length === 0 ? (
                            <p className={`px-3 py-2 text-xs ${
                              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              No names available
                            </p>
                          ) : (
                            selectedNames.map((guest) => (
                              <div
                                key={guest.id}
                                className={`px-3 py-1.5 text-xs truncate ${
                                  theme === 'dark'
                                    ? 'text-gray-300 hover:bg-gray-800'
                                    : 'text-gray-700 hover:bg-gray-50'
                                }`}
                                title={guest.name}
                              >
                                {guest.name}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className={`text-[10px] sm:text-xs ${
                  theme === 'dark' ? 'text-blue-300' : 'text-blue-600'
                }`}>
                  Choose an action to apply to selected guests
                </div>
              </div>
              <button
                type="button"
                onClick={onClearSelection}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium shrink-0 transition-colors border ${
                  theme === 'dark'
                    ? 'text-blue-200 border-blue-700/60 hover:bg-blue-900/40 hover:border-blue-600'
                    : 'text-blue-700 border-blue-200 hover:bg-white hover:border-blue-300'
                }`}
                title="Clear all selected guests"
              >
                <X className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Clear selection</span>
                <span className="sm:hidden">Clear</span>
              </button>
            </div>
            
            {/* Action Buttons Section */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-2 flex-1 sm:flex-initial">
              {/* Status Update Buttons - Stack on mobile, row on desktop */}
              <div className="flex flex-col sm:flex-row gap-2 flex-1 sm:flex-initial">
                <button
                  onClick={() => onBulkAction('update_status', 'accepted')}
                  className={`group px-3 py-2 text-xs sm:text-sm font-medium rounded-xl transition-all duration-200 sm:transform sm:hover:scale-105 shadow-sm sm:hover:shadow-md text-center ${
                    theme === 'dark'
                      ? 'bg-green-800/50 hover:bg-green-500 text-green-300 hover:text-white border border-green-600/50 hover:border-green-500'
                      : 'bg-green-100 hover:bg-green-500 text-green-700 hover:text-white border border-green-200 hover:border-green-500'
                  }`}
                >
                  Mark Confirmed
                </button>
                <button
                  onClick={() => onBulkAction('update_status', 'rejected')}
                  className={`group px-3 py-2 text-xs sm:text-sm font-medium rounded-xl transition-all duration-200 sm:transform sm:hover:scale-105 shadow-sm sm:hover:shadow-md text-center ${
                    theme === 'dark'
                      ? 'bg-red-800/50 hover:bg-red-500 text-red-300 hover:text-white border border-red-600/50 hover:border-red-500'
                      : 'bg-red-100 hover:bg-red-500 text-red-700 hover:text-white border border-red-200 hover:border-red-500'
                  }`}
                >
                  Mark Declined
                </button>
                <button
                  onClick={() => onBulkAction('update_status', 'pending')}
                  className={`group px-3 py-2 text-xs sm:text-sm font-medium rounded-xl transition-all duration-200 sm:transform sm:hover:scale-105 shadow-sm sm:hover:shadow-md text-center ${
                    theme === 'dark'
                      ? 'bg-yellow-800/50 hover:bg-yellow-500 text-yellow-300 hover:text-white border border-yellow-600/50 hover:border-yellow-500'
                      : 'bg-yellow-100 hover:bg-yellow-500 text-yellow-700 hover:text-white border border-yellow-200 hover:border-yellow-500'
                  }`}
                >
                  Mark Pending
                </button>
              </div>
              
              {/* Divider - Hidden on mobile */}
              <div className={`hidden sm:block w-px h-8 ${
                theme === 'dark' ? 'bg-gray-600/50' : 'bg-gray-300'
              }`}></div>
              
              {/* Remove Button */}
              <button
                onClick={() => onBulkAction('delete')}
                className={`group px-3 py-2 text-xs sm:text-sm font-medium rounded-xl transition-all duration-200 sm:transform sm:hover:scale-105 shadow-sm sm:hover:shadow-md text-center ${
                  theme === 'dark'
                    ? 'bg-red-800/50 hover:bg-red-600 text-red-300 hover:text-white border border-red-600/50 hover:border-red-600'
                    : 'bg-red-100 hover:bg-red-600 text-red-700 hover:text-white border border-red-200 hover:border-red-600'
                }`}
              >
                Remove Selected
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
